<script setup lang="ts">
import type { Property } from '~/core/entities/property';

import { PropertyPhotoCarouselDesktop } from '~/components/PropertyPhotoCarouselDesktop';
import { PropertyPhotoCarouselMobile } from '~/components/PropertyPhotoCarouselMobile';

// Note: These carousels are critical for LCP and use CSS-based visibility (hidden/xl:block, xl:hidden)
// Lazy hydration with media queries causes hydration mismatches because both are SSR-rendered
// but the wrapper elements interfere with DOM matching during hydration

interface PropertyHeroCarouselsProps {
	property: Property | null;
	isLoading: boolean;
}

interface PropertyHeroCarouselsEmits {
	'photo-clicked': [
		payload: {
			photoIndex: number;
			photoUrl: string;
			clickType: 'photo' | 'cta';
		}
	];
}

const props = defineProps<PropertyHeroCarouselsProps>();
defineEmits<PropertyHeroCarouselsEmits>();

const { ft_poc_videos } = useFeatureFlags();

/**
 * Gets the featured video URL and thumbnail for the property.
 * Uses the first featured video if available.
 * Only returns the URL when ft_poc_videos feature flag is enabled.
 */
const featuredVideo = computed(() =>
	ft_poc_videos ? props.property?.featuredVideos?.[0] : undefined
);

const featuredVideoUrl = computed(() => featuredVideo.value?.url);
const featuredVideoPosterUrl = computed(() => featuredVideo.value?.thumbnail);
const featuredVideoWidth = computed(() => featuredVideo.value?.width);
const featuredVideoHeight = computed(() => featuredVideo.value?.height);
</script>

<template>
	<!-- Photo Carousels - both rendered with CSS-based visibility for LCP optimization -->
	<PropertyPhotoCarouselDesktop
		:property="property"
		:images="property?.images || []"
		:is-loading="isLoading"
		:video-url="featuredVideoUrl"
		:poster-url="featuredVideoPosterUrl"
		:video-width="featuredVideoWidth"
		:video-height="featuredVideoHeight"
		section-name="hero section"
		@photo-clicked="$emit('photo-clicked', $event)"
	/>

	<PropertyPhotoCarouselMobile
		:property="property"
		:images="property?.images || []"
		:is-loading="isLoading"
		:video-url="featuredVideoUrl"
		:poster-url="featuredVideoPosterUrl"
		:video-width="featuredVideoWidth"
		:video-height="featuredVideoHeight"
		section-name="hero section"
		@photo-clicked="$emit('photo-clicked', $event)"
	/>
</template>
